import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Trash2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Field, Panel } from "@/components/admin/shared";
import { VideoPlayer } from "@/components/VideoPlayer";

const FacebookLink = ({ onAdded, category }) => {
  const [f, setF] = useState({ title: "", url: "", description: "" });
  const add = async () => {
    try {
      await api.post("/admin/videos/link", { ...f, category });
      toast.success(category === "testimonio" ? "Testimonio agregado" : "Video agregado a la galería");
      setF({ title: "", url: "", description: "" });
      onAdded();
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <Panel title="Agregar video de Facebook">
      <ol className="mb-4 list-decimal space-y-1 pl-5 text-sm text-ink2">
        <li>En Facebook abre el video, toca los tres puntos (…) y elige «Copiar enlace».</li>
        <li>Pégalo aquí con un título y presiona Agregar.</li>
      </ol>
      <div className="space-y-4">
        <Field id="fbvideo-title-input" label="Título (opcional)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={category === "testimonio" ? "Ej. Testimonio de Don Luis" : "Ej. Cómo saber si tus amortiguadores fallan"} />
        <Field id="fbvideo-url-input" label="Enlace del video *" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://www.facebook.com/.../videos/..." />
        <Field id="fbvideo-description-input" label="Descripción" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <p className="text-xs text-mute2">El video debe ser público en Facebook para que se vea en la página.</p>
        <button className="btn-gold w-full" disabled={!f.url.trim()} onClick={add} data-testid="fbvideo-add-btn">{category === "testimonio" ? "Agregar a Testimonios" : "Agregar a la galería"}</button>
      </div>
    </Panel>
  );
};

const CATS = [["instructivo", "Videos instructivos (Galería)"], ["testimonio", "Testimonios de clientes"]];

export const VideosTab = () => {
  const [cat, setCat] = useState("instructivo");
  const [videos, setVideos] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = () => api.get(`/videos?category=${cat}`).then((r) => setVideos(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [cat]);
  const upload = async () => {
    const file = ref.current?.files?.[0];
    if (!file) return toast.error("Elige un video");
    const fd = new FormData();
    fd.append("title", title);
    fd.append("description", description);
    fd.append("category", cat);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/admin/videos", fd);
      toast.success("Video publicado en la galería");
      setTitle(""); setDescription(""); ref.current.value = "";
      load();
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };
  const remove = async (v) => {
    if (!window.confirm(`¿Quitar ${v.title ? `"${v.title}"` : "este video"} de la página?`)) return;
    await api.delete(`/admin/videos/${v.id}`);
    load();
  };
  return (
    <div className="space-y-6">
    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Tipo de video">
      {CATS.map(([id, label]) => (
        <button key={id} role="tab" aria-selected={cat === id} onClick={() => setCat(id)} data-testid={`videos-cat-${id}`}
          className={`rounded-full border px-4 py-2 text-sm font-semibold ${cat === id ? "border-green bg-green text-white" : "border-line text-ink2 hover:border-green"}`}>{label}</button>
      ))}
    </div>
    <div className="grid gap-6 lg:grid-cols-[380px,1fr]">
      <div className="space-y-6">
      <FacebookLink onAdded={load} category={cat} />
      <Panel title="Subir video desde tu celular o computadora">
        <div className="space-y-4">
          <Field id="video-title-input" label="Título (opcional)" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Cambio de amortiguadores" />
          <Field id="video-description-input" label="Descripción" value={description} onChange={(e) => setDescription(e.target.value)} />
          <input ref={ref} type="file" accept="video/mp4,video/webm,video/quicktime" aria-label="Archivo de video" className="block w-full text-sm" data-testid="video-file-input" />
          <p className="text-xs text-mute2">MP4, WEBM o MOV de hasta 100 MB.</p>
          <button className="btn-gold w-full" disabled={busy} onClick={upload} data-testid="video-upload-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {busy ? "Subiendo..." : "Publicar video"}</button>
        </div>
      </Panel>
      </div>
      <Panel title={cat === "testimonio" ? "Testimonios publicados" : "Videos en la galería"}>
        <div className="grid gap-4 sm:grid-cols-2" data-testid="admin-videos-list">
          {videos.map((v) => (
            <div key={v.id} className="overflow-hidden rounded-xl border border-line">
              <VideoPlayer v={v} />
              <div className="flex items-start justify-between gap-2 p-3 text-sm">
                <div><p className="font-semibold">{v.title || "Sin título"}</p><p className="text-xs text-mute">{v.kind === "facebook" ? "Desde Facebook" : `${(v.size / 1048576).toFixed(1)} MB`}</p></div>
                <button onClick={() => remove(v)} aria-label="Quitar video" className="text-red-500" data-testid={`video-delete-${v.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {!videos.length && <p className="text-mute">Aún no hay videos.</p>}
        </div>
      </Panel>
    </div>
    </div>
  );
};
